import React, { type ReactElement, type ComponentPropsWithoutRef, memo } from 'react';
import {
	MULTIUSERPICKER_FIELD_TYPE,
	USERPICKER_FIELD_TYPE,
	DESCRIPTION_FIELD_TYPE,
	ENVIRONMENT_FIELD_TYPE,
	TEXT_AREA_CF_TYPE,
} from '@jira/project-settings__field-types/src/fieldTypesConfig.tsx';
import Icon from '@atlaskit/icon';
import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import AppIcon from '@atlaskit/icon/core/app';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import AttachmentIcon from '@atlaskit/icon/core/attachment';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import CheckboxCheckedIcon from '@atlaskit/icon/core/checkbox-checked';
import ClockIcon from '@atlaskit/icon/core/clock';
import CommentIcon from '@atlaskit/icon/core/comment';
import CrossIcon from '@atlaskit/icon/core/cross';
import DataNumberIcon from '@atlaskit/icon/core/data-number';
import FieldDropdownIcon from '@atlaskit/icon/core/field-dropdown';
import FieldIcon from '@atlaskit/icon/core/field';
import FolderClosedIcon from '@atlaskit/icon/core/folder-closed';
import GoalIcon from '@atlaskit/icon/core/goal';
import ListBulletedIcon from '@atlaskit/icon/core/list-bulleted';
import MenuIcon from '@atlaskit/icon/core/menu';
import OfficeBuildingIcon from '@atlaskit/icon/core/office-building';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import PersonIcon from '@atlaskit/icon/core/person';
import PersonAddedIcon from '@atlaskit/icon/core/person-added';
import ProjectIcon from '@atlaskit/icon/core/project';
import RadioCheckedIcon from '@atlaskit/icon/core/radio-checked';
import ReleaseIcon from '@atlaskit/icon/core/release';
import ScreenIcon from '@atlaskit/icon/core/screen';
import BranchesIcon from '@atlaskit/icon/core/branch';
import StopwatchIcon from '@atlaskit/icon/core/stopwatch';
import TagIcon from '@atlaskit/icon/core/tag';
import TeamsIcon from '@atlaskit/icon/core/teams';
import DataFormulaIcon from '@atlaskit/icon-lab/core/data-formula';
import SortOptionsIcon from '@atlaskit/icon-lab/core/sort-options';
import TextLetterCaseIcon from '@atlaskit/icon-lab/core/text-letter-case';
import { token } from '@atlaskit/tokens';
import type {
	IgnoredFieldCheck,
	CheckFieldTypeDefinitions,
	CheckedLegacyFieldTypeDefinitions,
} from '@atlassian/jira-platform-field-config/src/common/types/checked-fields.tsx';
import {
	ASSETS_CF_TYPE,
	ASSIGNEE_TYPE as ASSIGNEE_FIELD_TYPE,
	ATTACHMENT_TYPE as ATTACHMENT_FIELD_TYPE,
	DATE_CF_TYPE,
	DATETIME_CF_TYPE,
	DUE_DATE_TYPE as DUE_DATE_FIELD_TYPE,
	FIX_VERSIONS_TYPE as FIX_VERSIONS_FIELD_TYPE,
	FLAGGED_CF_TYPE,
	GROUP_CF_TYPE,
	ISSUE_LINKS_TYPE as ISSUE_LINKS_FIELD_TYPE,
	LABELS_CF_TYPE,
	LABELS_TYPE as LABELS_FIELD_TYPE,
	MULTI_CHECKBOXES_CF_TYPE,
	MULTI_GROUP_CF_TYPE,
	MULTI_USER_CF_TYPE,
	MULTI_VERSION_CF_TYPE,
	NUMBER_CF_TYPE,
	STORY_POINT_ESTIMATE_CF_TYPE,
	ORGANIZATIONS_CF_TYPE,
	PARENT_CF_TYPE,
	PEOPLE_CF_TYPE,
	PRIORITY_TYPE as PRIORITY_FIELD_TYPE,
	PROJECT_PICKER_CF_TYPE,
	RADIO_BUTTONS_CF_TYPE,
	REPORTER_TYPE as REPORTER_FIELD_TYPE,
	REQUEST_PARTICIPANTS_CF_TYPE,
	SELECT_CF_TYPE,
	CASCADING_SELECT_CF_TYPE,
	SERVICE_ENTITY_CF_TYPE,
	SPRINT_TYPE as SPRINT_CF_GLOBAL_TYPE,
	SUMMARY_TYPE as SUMMARY_FIELD_TYPE,
	TEAM_CF_TYPE,
	TEAMS_PLATFORM_CF_TYPE,
	TEXT_CF_TYPE,
	TIME_ESTIMATE_CONFIG_TYPE as TIME_ESTIMATE_FIELD_TYPE,
	TIME_TRACKING_TYPE as TIME_TRACKING_FIELD_TYPE,
	TIME_ESTIMATE_TYPE as ORIGINAL_TIME_ESTIMATE_FIELD_TYPE,
	URL_CF_TYPE,
	USER_CF_TYPE,
	VERSION_CF_TYPE,
	STATUS_TYPE as STATUS_FIELD_TYPE,
	COMMENT_TYPE as COMMENT_FIELD_TYPE,
	APPROVERS_LIST_CF_TYPE,
	APPROVALS_CF_TYPE as APPROVAL_DATA_FIELD_TYPE,
	MESSAGE_VIEW_CF_TYPE,
	MESSAGE_EDIT_CF_TYPE,
	MULTI_SELECT_CF_TYPE,
	CATEGORY_TYPE,
	ENTITLEMENT_CF_TYPE,
	SENTIMENT_CF_TYPE,
	GOALS_CF_TYPE,
	ORGANIZATION_CF_TYPE,
	BASELINE_START_CF_TYPE,
	BASELINE_END_CF_TYPE,
	ATLAS_PROJECT_CF_TYPE,
	FORMULA_NUMBER_CF_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	addonGlyph,
	arrowRightGlyph,
	attachmentGlyph,
	bulletListGlyph,
	checkboxGlyph,
	closeGlyph,
	commentGlyph,
	dateGlyph,
	dropdownGlyph,
	labelGlyph,
	numberGlyph,
	officeBuildingGlyph,
	paragraphGlyph,
	peopleGlyph,
	peopleGroupGlyph,
	priorityGlyph,
	radioGlyph,
	screenGlyph,
	shipGlyph,
	shortTextGlyph,
	timeStampGlyph,
	timeTrackingGlyph,
	originalTimeEstimateGlyph,
	appAccessGlyph,
	folderGlyph,
	doubleDropdownGlyph,
	DoubleDropdownCustomIcon,
	customFieldGlyph,
	jwmCategoryGlyph,
} from './shortTextGlyph.tsx';

type ResizedIconProps = {
	// eslint-disable-next-line jira/react/handler-naming
	glyph: (props: { [key: string]: string }) => ReactElement;
};

// @ts-expect-error - TS2322 - Type '(props: { [key: string]: string; }) => ReactElement<any, string | JSXElementConstructor<any>>' is not assignable to type 'ComponentType<CustomGlyphProps> | undefined'.
// eslint-disable-next-line @atlaskit/design-system/no-custom-icons
const ResizedIcon = ({ glyph }: ResizedIconProps) => <Icon label="" glyph={glyph} size="medium" />;

type IgnoredFieldChecks<T> = {
	['com.atlassian.jira.modules.servicemanagement.responders-entity:responders-entity-field-cftype']: IgnoredFieldCheck<T>;
	['com.atlassian.jira.plugin.system.customfieldtypes:multiselect']: IgnoredFieldCheck<T>;
	['com.atlassian.jira.plugin.system.customfieldtypes:readonlyfield']: IgnoredFieldCheck<T>;
	['com.atlassian.jira.plugin.system.customfieldtypes:storypoint']: IgnoredFieldCheck<T>;
	['com.atlassian.servicedesk.approvals-plugin:sd-approvals']: IgnoredFieldCheck<T>;
	['com.atlassian.jira.plugins.cmdb:cmdb-object-cftype']: IgnoredFieldCheck<T>;
	['com.atlassian.jira.toolkit:participants']: IgnoredFieldCheck<T>;
	['com.atlassian.servicedesk.servicedesk-lingo-integration-plugin:sd-request-language']: IgnoredFieldCheck<T>;
	['com.atlassian.servicedesk:sd-request-feedback']: IgnoredFieldCheck<T>;
	['com.atlassian.servicedesk:sd-request-feedback-date']: IgnoredFieldCheck<T>;
	['com.atlassian.servicedesk:sd-sla-field']: IgnoredFieldCheck<T>;
	['com.atlassian.servicedesk:vp-origin']: IgnoredFieldCheck<T>;
	['com.pyxis.greenhopper.jira:gh-epic-color']: IgnoredFieldCheck<T>;
	['com.pyxis.greenhopper.jira:gh-epic-label']: IgnoredFieldCheck<T>;
	['com.pyxis.greenhopper.jira:gh-epic-link']: IgnoredFieldCheck<T>;
	['com.pyxis.greenhopper.jira:gh-epic-status']: IgnoredFieldCheck<T>;
	['com.pyxis.greenhopper.jira:gh-lexo-rank']: IgnoredFieldCheck<T>;
	['com.pyxis.greenhopper.jira:jsw-issue-color']: IgnoredFieldCheck<T>;
	['com.atlassian.jira.ext.charting:firstresponsedate']: IgnoredFieldCheck<T>;
	['com.atlassian.jconnect.jconnect-plugin:uuid']: IgnoredFieldCheck<T>;
	['com.atlassian.jconnect.jconnect-plugin:location']: IgnoredFieldCheck<T>;
	['com.atlassian.jira.plugins.jira-development-integration-plugin:devsummarycf']: IgnoredFieldCheck<T>;
	['com.atlassian.jira.plugin.system.customfieldtypes:atlassian-team']: IgnoredFieldCheck<T>;
	['com.atlassian.jira.ext.charting:timeinstatus']: IgnoredFieldCheck<T>;
	['com.atlassian.jira.toolkit:assigneedomain']: IgnoredFieldCheck<T>;
	['com.atlassian.jira.toolkit:LastCommentDate']: IgnoredFieldCheck<T>;
	['com.atlassian.jira.toolkit:attachments']: IgnoredFieldCheck<T>;
	['com.atlassian.jira.toolkit:comments']: IgnoredFieldCheck<T>;
	['com.atlassian.jira.toolkit:dayslastcommented']: IgnoredFieldCheck<T>;
	['com.atlassian.jira.toolkit:lastupdateorcommenter']: IgnoredFieldCheck<T>;
	['com.atlassian.jira.toolkit:lastusercommented']: IgnoredFieldCheck<T>;
	['com.atlassian.jira.toolkit:reporterdomain']: IgnoredFieldCheck<T>;
	['com.atlassian.jira.toolkit:userproperty']: IgnoredFieldCheck<T>;
	aggregateprogress: IgnoredFieldCheck<T>;
	aggregatetimeestimate: IgnoredFieldCheck<T>;
	aggregatetimeoriginalestimate: IgnoredFieldCheck<T>;
	assignee: IgnoredFieldCheck<T>;
	components: IgnoredFieldCheck<T>;
	created: IgnoredFieldCheck<T>;
	creator: IgnoredFieldCheck<T>;
	description: IgnoredFieldCheck<T>;
	devSummary: IgnoredFieldCheck<T>;
	issuekey: IgnoredFieldCheck<T>;
	issuetype: IgnoredFieldCheck<T>;
	parent: IgnoredFieldCheck<T>;
	priority: IgnoredFieldCheck<T>;
	releases: IgnoredFieldCheck<T>;
	reporter: IgnoredFieldCheck<T>;
	resolution: IgnoredFieldCheck<T>;
	resolutiondate: IgnoredFieldCheck<T>;
	status: IgnoredFieldCheck<T>;
	summary: IgnoredFieldCheck<T>;
	updated: IgnoredFieldCheck<T>;
	versions: IgnoredFieldCheck<T>;
	watches: IgnoredFieldCheck<T>;
	workratio: IgnoredFieldCheck<T>;
	worklog: IgnoredFieldCheck<T>;
	votes: IgnoredFieldCheck<T>;
	timespent: IgnoredFieldCheck<T>;
	subtasks: IgnoredFieldCheck<T>;
	statuscategorychangedate: IgnoredFieldCheck<T>;
	project: IgnoredFieldCheck<T>;
	security: IgnoredFieldCheck<T>;
	progress: IgnoredFieldCheck<T>;
	lastViewed: IgnoredFieldCheck<T>;
	issuerestriction: IgnoredFieldCheck<T>;
};

const TeamCfTypeIcon = TeamsIcon;

const AddonIcon = () =>
	fg('enghealth-custom-icon-migration') ? (
		<AppIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={addonGlyph} />
	);

const ArrowRightIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<ArrowRightIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={arrowRightGlyph} />
	);

const AttachmentIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<AttachmentIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={attachmentGlyph} />
	);

const BulletListIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<ListBulletedIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={bulletListGlyph} />
	);

const CheckboxIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<CheckboxCheckedIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={checkboxGlyph} />
	);

const CloseIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<CrossIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={closeGlyph} />
	);

const CommentIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<CommentIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={commentGlyph} />
	);

const DateIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<CalendarIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={dateGlyph} />
	);

const DropdownIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<FieldDropdownIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={dropdownGlyph} />
	);

const DoubleDropdownIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<DoubleDropdownCustomIcon label="" primaryColor={token('color.icon')} />
	) : (
		<ResizedIcon glyph={doubleDropdownGlyph} />
	);

const LabelIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<TagIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={labelGlyph} />
	);

const OfficeBuildingIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<OfficeBuildingIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={officeBuildingGlyph} />
	);

const ParagraphIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<AlignTextLeftIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={paragraphGlyph} />
	);

const PeopleIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<PersonIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={peopleGlyph} />
	);

const PeopleGroupIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<PeopleGroupIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={peopleGroupGlyph} />
	);

const PriorityIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<SortOptionsIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={priorityGlyph} />
	);

const RadioIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<RadioCheckedIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={radioGlyph} />
	);

const ScreenIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<ScreenIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={screenGlyph} />
	);

const ShipIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<ReleaseIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={shipGlyph} />
	);

const ShortTextIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<TextLetterCaseIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={shortTextGlyph} />
	);

const TimeStampIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<ClockIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={timeStampGlyph} />
	);

const TimeTrackingIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<StopwatchIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={timeTrackingGlyph} />
	);

const OriginalTimeEstimateIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<ClockIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={originalTimeEstimateGlyph} />
	);

const AppAccessIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<PersonAddedIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={appAccessGlyph} />
	);

const FolderIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<FolderClosedIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={folderGlyph} />
	);

const CustomFieldIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<FieldIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={customFieldGlyph} />
	);

const JwmCategoryIconWrapper = () =>
	fg('enghealth-custom-icon-migration') ? (
		<MenuIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={jwmCategoryGlyph} />
	);

const NumberIconWrapper = () =>
	!fg('enghealth-custom-icon-migration') ? (
		<DataNumberIcon label="" spacing="spacious" color={token('color.icon')} />
	) : (
		<ResizedIcon glyph={numberGlyph} />
	);

const checkedFieldTypesIcons = {
	[NUMBER_CF_TYPE]: <NumberIconWrapper />,
	[STORY_POINT_ESTIMATE_CF_TYPE]: <NumberIconWrapper />,
	[LABELS_CF_TYPE]: <LabelIconWrapper />,
	[DATE_CF_TYPE]: <DateIconWrapper />,
	[DATETIME_CF_TYPE]: <TimeStampIconWrapper />,
	[RADIO_BUTTONS_CF_TYPE]: <RadioIconWrapper />,
	[SELECT_CF_TYPE]: <DropdownIconWrapper />,
	[MULTI_SELECT_CF_TYPE]: <DropdownIconWrapper />,
	[CASCADING_SELECT_CF_TYPE]: <DoubleDropdownIconWrapper />,
	[MULTI_VERSION_CF_TYPE]: <DropdownIconWrapper />,
	[VERSION_CF_TYPE]: <DropdownIconWrapper />,
	[GROUP_CF_TYPE]: <DropdownIconWrapper />,
	[MULTI_GROUP_CF_TYPE]: <DropdownIconWrapper />,
	[USER_CF_TYPE]: <PeopleIconWrapper />,
	[USERPICKER_FIELD_TYPE]: <PeopleIconWrapper />,
	[MULTI_USER_CF_TYPE]: <PeopleIconWrapper />,
	[MULTIUSERPICKER_FIELD_TYPE]: <PeopleIconWrapper />,
	[PEOPLE_CF_TYPE]: <PeopleIconWrapper />,
	[FLAGGED_CF_TYPE]: <TimeStampIconWrapper />,
	[URL_CF_TYPE]: <CustomFieldIconWrapper />,
	[TEAM_CF_TYPE]: <CustomFieldIconWrapper />,
	// <ResizedIcon /> was introduced when SWIFT had a limited number of supported field types,
	// but it's no longer a scalable option. We should be using icons directly provided by @atlaskit,
	// and if it does not match our design constraints, then we'll need to investigate contributing to a new @atlaskit icon set.
	[TEAMS_PLATFORM_CF_TYPE]: <TeamCfTypeIcon spacing="spacious" label="" />,
	[PARENT_CF_TYPE]: <CustomFieldIconWrapper />,
	[BASELINE_START_CF_TYPE]: <DateIconWrapper />,
	[BASELINE_END_CF_TYPE]: <DateIconWrapper />,
	[SPRINT_CF_GLOBAL_TYPE]: <ShipIconWrapper />,
	[REQUEST_PARTICIPANTS_CF_TYPE]: <PeopleGroupIconWrapper />,
	[ORGANIZATIONS_CF_TYPE]: <OfficeBuildingIconWrapper />,
	[REPORTER_FIELD_TYPE]: <PeopleIconWrapper />,
	[ATTACHMENT_FIELD_TYPE]: <AttachmentIconWrapper />,
	[ASSIGNEE_FIELD_TYPE]: <PeopleIconWrapper />,
	[LABELS_FIELD_TYPE]: <LabelIconWrapper />,
	[PRIORITY_FIELD_TYPE]: <PriorityIconWrapper />,
	[DUE_DATE_FIELD_TYPE]: <DateIconWrapper />,
	[TIME_ESTIMATE_FIELD_TYPE]: <TimeStampIconWrapper />,
	[TIME_TRACKING_FIELD_TYPE]: <TimeTrackingIconWrapper />,
	[ORIGINAL_TIME_ESTIMATE_FIELD_TYPE]: <OriginalTimeEstimateIconWrapper />,
	[ASSETS_CF_TYPE]: <ScreenIconWrapper />,
	[SERVICE_ENTITY_CF_TYPE]: <LabelIconWrapper />,
	[STATUS_FIELD_TYPE]: <ArrowRightIconWrapper />,
	[SUMMARY_FIELD_TYPE]: <ShortTextIconWrapper />,
	[DESCRIPTION_FIELD_TYPE]: <ParagraphIconWrapper />,
	[TEXT_CF_TYPE]: <ShortTextIconWrapper />,
	[TEXT_AREA_CF_TYPE]: <ParagraphIconWrapper />,
	[MULTI_CHECKBOXES_CF_TYPE]: <CheckboxIconWrapper />,
	[ISSUE_LINKS_FIELD_TYPE]: <CustomFieldIconWrapper />,
	[ENVIRONMENT_FIELD_TYPE]: <ParagraphIconWrapper />,
	[FIX_VERSIONS_FIELD_TYPE]: <DropdownIconWrapper />,
	[COMMENT_FIELD_TYPE]: <CommentIconWrapper />,
	[APPROVERS_LIST_CF_TYPE]: <AppAccessIconWrapper />,
	[APPROVAL_DATA_FIELD_TYPE]: <AppAccessIconWrapper />,
	[PROJECT_PICKER_CF_TYPE]: <FolderIconWrapper />,
	[MESSAGE_EDIT_CF_TYPE]: <ParagraphIconWrapper />,
	[MESSAGE_VIEW_CF_TYPE]: <ParagraphIconWrapper />,
	[CATEGORY_TYPE]: <JwmCategoryIconWrapper />,
	[ENTITLEMENT_CF_TYPE]: <DropdownIconWrapper />,
	[SENTIMENT_CF_TYPE]: <CustomFieldIconWrapper />,
	[GOALS_CF_TYPE]: <GoalIcon label="" spacing="spacious" color={token('color.icon')} />,
	[ATLAS_PROJECT_CF_TYPE]: <ProjectIcon label="" spacing="spacious" color={token('color.icon')} />,
	[ORGANIZATION_CF_TYPE]: <CustomFieldIconWrapper />,
	[FORMULA_NUMBER_CF_TYPE]: (
		<DataFormulaIcon label="" spacing="spacious" color={token('color.icon')} />
	),
} as const;

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
({ ...checkedFieldTypesIcons }) as CheckFieldTypeDefinitions<
	CheckedLegacyFieldTypeDefinitions<
		ReactElement<ComponentPropsWithoutRef<typeof ResizedIcon | typeof Icon>>
	>,
	IgnoredFieldChecks<ReactElement<ComponentPropsWithoutRef<typeof ResizedIcon | typeof Icon>>>
>;

/**
 * @deprecated - Use checkedFieldTypesGlyphs or StrictFieldTypeIcon instead
 */
export const fieldTypesIcons: {
	[key: string]: ReactElement<ComponentPropsWithoutRef<typeof ResizedIcon | typeof Icon>>;
} = checkedFieldTypesIcons;

export const panelIcons: {
	[key: string]: ReactElement;
} = {
	development: <BranchesIcon label="" spacing="spacious" />,
	devSummary: <BranchesIcon label="" spacing="spacious" />,
	timetracking: <StopwatchIcon label="" spacing="spacious" />,
};

/**
 * @deprecated - consider using allFieldTypeGlyphs or FieldTypeIcon instead
 */
const allIcons: {
	[key: string]: ReactElement<ComponentPropsWithoutRef<typeof ResizedIcon | typeof Icon>>;
} = {
	...fieldTypesIcons,
	text: <ParagraphIconWrapper />,
	number: <NumberIconWrapper />,
	date: <DateIconWrapper />,
	datetime: <TimeStampIconWrapper />,
	dropdown: <DropdownIconWrapper />,
	addon: <AddonIcon />,
	checkbox: <CheckboxIconWrapper />,
	user: <PeopleIconWrapper />,
	radio: <RadioIconWrapper />,
	time: <TimeStampIconWrapper />,
	message: <CommentIconWrapper />,
	bulletList: <BulletListIconWrapper />,
	label: <LabelIconWrapper />,
	labelpicker: <LabelIconWrapper />,
	close: <CloseIconWrapper />,
	assetspicker: <ScreenIconWrapper />,
	select: <DropdownIconWrapper />,
	multiselect: <DropdownIconWrapper />,
	cascadingselect: <DoubleDropdownIconWrapper />,
	multigrouppicker: <DropdownIconWrapper />,
	grouppicker: <DropdownIconWrapper />,
	multiversion: <DropdownIconWrapper />,
	version: <DropdownIconWrapper />,
	projectpicker: <DropdownIconWrapper />,
	customField: <CustomFieldIconWrapper />,
};

const checkedFieldTypesGlyphs: {
	[key: string]: (props: ComponentPropsWithoutRef<'svg'>) => JSX.Element;
} = {
	[NUMBER_CF_TYPE]: numberGlyph,
	[STORY_POINT_ESTIMATE_CF_TYPE]: numberGlyph,
	[LABELS_CF_TYPE]: labelGlyph,
	[DATE_CF_TYPE]: dateGlyph,
	[DATETIME_CF_TYPE]: timeStampGlyph,
	[RADIO_BUTTONS_CF_TYPE]: radioGlyph,
	[SELECT_CF_TYPE]: dropdownGlyph,
	[MULTI_SELECT_CF_TYPE]: dropdownGlyph,
	[CASCADING_SELECT_CF_TYPE]: doubleDropdownGlyph,
	[MULTI_VERSION_CF_TYPE]: dropdownGlyph,
	[VERSION_CF_TYPE]: dropdownGlyph,
	[GROUP_CF_TYPE]: dropdownGlyph,
	[MULTI_GROUP_CF_TYPE]: dropdownGlyph,
	[USER_CF_TYPE]: peopleGlyph,
	[USERPICKER_FIELD_TYPE]: peopleGlyph,
	[MULTI_USER_CF_TYPE]: peopleGlyph,
	[MULTIUSERPICKER_FIELD_TYPE]: peopleGlyph,
	[PEOPLE_CF_TYPE]: peopleGlyph,
	[FLAGGED_CF_TYPE]: timeStampGlyph,
	[URL_CF_TYPE]: customFieldGlyph,
	[TEAM_CF_TYPE]: customFieldGlyph,
	[PARENT_CF_TYPE]: customFieldGlyph,
	[BASELINE_START_CF_TYPE]: dateGlyph,
	[BASELINE_END_CF_TYPE]: dateGlyph,
	[SPRINT_CF_GLOBAL_TYPE]: shipGlyph,
	[REQUEST_PARTICIPANTS_CF_TYPE]: peopleGroupGlyph,
	[ORGANIZATIONS_CF_TYPE]: officeBuildingGlyph,
	[REPORTER_FIELD_TYPE]: peopleGlyph,
	[ATTACHMENT_FIELD_TYPE]: attachmentGlyph,
	[ASSIGNEE_FIELD_TYPE]: peopleGlyph,
	[LABELS_FIELD_TYPE]: labelGlyph,
	[PRIORITY_FIELD_TYPE]: priorityGlyph,
	[DUE_DATE_FIELD_TYPE]: dateGlyph,
	[TIME_ESTIMATE_FIELD_TYPE]: timeStampGlyph,
	[TIME_TRACKING_FIELD_TYPE]: timeTrackingGlyph,
	[ORIGINAL_TIME_ESTIMATE_FIELD_TYPE]: originalTimeEstimateGlyph,
	[ASSETS_CF_TYPE]: screenGlyph,
	[SERVICE_ENTITY_CF_TYPE]: labelGlyph,
	[STATUS_FIELD_TYPE]: arrowRightGlyph,
	[SUMMARY_FIELD_TYPE]: shortTextGlyph,
	[DESCRIPTION_FIELD_TYPE]: paragraphGlyph,
	[TEXT_CF_TYPE]: shortTextGlyph,
	[TEXT_AREA_CF_TYPE]: paragraphGlyph,
	[MULTI_CHECKBOXES_CF_TYPE]: checkboxGlyph,
	[ISSUE_LINKS_FIELD_TYPE]: customFieldGlyph,
	[ENVIRONMENT_FIELD_TYPE]: paragraphGlyph,
	[FIX_VERSIONS_FIELD_TYPE]: dropdownGlyph,
	[COMMENT_FIELD_TYPE]: commentGlyph,
	[APPROVERS_LIST_CF_TYPE]: appAccessGlyph,
	[APPROVAL_DATA_FIELD_TYPE]: appAccessGlyph,
	[PROJECT_PICKER_CF_TYPE]: folderGlyph,
	[MESSAGE_EDIT_CF_TYPE]: paragraphGlyph,
	[MESSAGE_VIEW_CF_TYPE]: paragraphGlyph,
	[CATEGORY_TYPE]: jwmCategoryGlyph,
	[ENTITLEMENT_CF_TYPE]: dropdownGlyph,
	[SENTIMENT_CF_TYPE]: customFieldGlyph,
	[ORGANIZATION_CF_TYPE]: customFieldGlyph,
	[ATLAS_PROJECT_CF_TYPE]: customFieldGlyph,
} as const;

export const allFieldTypeGlyphs: {
	[key: string]: (props: ComponentPropsWithoutRef<'svg'>) => JSX.Element;
} = {
	...checkedFieldTypesGlyphs,
	text: paragraphGlyph,
	number: numberGlyph,
	date: dateGlyph,
	datetime: timeStampGlyph,
	dropdown: dropdownGlyph,
	addon: addonGlyph,
	checkbox: checkboxGlyph,
	user: peopleGlyph,
	radio: radioGlyph,
	time: timeStampGlyph,
	message: commentGlyph,
	bulletList: bulletListGlyph,
	label: labelGlyph,
	labelpicker: labelGlyph,
	close: closeGlyph,
	assetspicker: screenGlyph,
	select: dropdownGlyph,
	multiselect: dropdownGlyph,
	cascadingselect: doubleDropdownGlyph,
	multigrouppicker: dropdownGlyph,
	grouppicker: dropdownGlyph,
	multiversion: dropdownGlyph,
	version: dropdownGlyph,
	projectpicker: dropdownGlyph,
	customField: customFieldGlyph,
};

type FieldTypeIconProps = {
	type: string;
	size?: 'small' | 'medium';
	testId?: string;
};

const newIconMapping: {
	[key: string]: (size: 'small' | 'medium', testId?: string) => ReactElement;
} = {
	[NUMBER_CF_TYPE]: (size, testId) => (
		<DataNumberIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[STORY_POINT_ESTIMATE_CF_TYPE]: (size, testId) => (
		<DataNumberIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[LABELS_CF_TYPE]: (size, testId) => (
		<TagIcon label="" spacing="spacious" testId={testId} color={token('color.icon')} size={size} />
	),
	[LABELS_FIELD_TYPE]: (size, testId) => (
		<TagIcon label="" spacing="spacious" testId={testId} color={token('color.icon')} size={size} />
	),
	[SERVICE_ENTITY_CF_TYPE]: (size, testId) => (
		<TagIcon label="" spacing="spacious" testId={testId} color={token('color.icon')} size={size} />
	),
	[DATE_CF_TYPE]: (size, testId) => (
		<CalendarIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[DUE_DATE_FIELD_TYPE]: (size, testId) => (
		<CalendarIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[BASELINE_START_CF_TYPE]: (size, testId) => (
		<CalendarIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[BASELINE_END_CF_TYPE]: (size, testId) => (
		<CalendarIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[DATETIME_CF_TYPE]: (size, testId) => (
		<ClockIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[FLAGGED_CF_TYPE]: (size, testId) => (
		<ClockIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[TIME_ESTIMATE_FIELD_TYPE]: (size, testId) => (
		<ClockIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[ORIGINAL_TIME_ESTIMATE_FIELD_TYPE]: (size, testId) => (
		<ClockIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[RADIO_BUTTONS_CF_TYPE]: (size, testId) => (
		<RadioCheckedIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[SELECT_CF_TYPE]: (size, testId) => (
		<FieldDropdownIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[MULTI_SELECT_CF_TYPE]: (size, testId) => (
		<FieldDropdownIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[MULTI_VERSION_CF_TYPE]: (size, testId) => (
		<FieldDropdownIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[VERSION_CF_TYPE]: (size, testId) => (
		<FieldDropdownIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[GROUP_CF_TYPE]: (size, testId) => (
		<FieldDropdownIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[MULTI_GROUP_CF_TYPE]: (size, testId) => (
		<FieldDropdownIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[FIX_VERSIONS_FIELD_TYPE]: (size, testId) => (
		<FieldDropdownIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[ENTITLEMENT_CF_TYPE]: (size, testId) => (
		<FieldDropdownIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[CASCADING_SELECT_CF_TYPE]: () => (
		<DoubleDropdownCustomIcon label="" primaryColor={token('color.icon')} />
	),
	[USER_CF_TYPE]: (size, testId) => (
		<PersonIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[USERPICKER_FIELD_TYPE]: (size, testId) => (
		<PersonIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[MULTI_USER_CF_TYPE]: (size, testId) => (
		<PersonIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[MULTIUSERPICKER_FIELD_TYPE]: (size, testId) => (
		<PersonIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[PEOPLE_CF_TYPE]: (size, testId) => (
		<PersonIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[REPORTER_FIELD_TYPE]: (size, testId) => (
		<PersonIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[ASSIGNEE_FIELD_TYPE]: (size, testId) => (
		<PersonIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[URL_CF_TYPE]: (size, testId) => (
		<FieldIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[TEAM_CF_TYPE]: (size, testId) => (
		<FieldIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[PARENT_CF_TYPE]: (size, testId) => (
		<FieldIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[ISSUE_LINKS_FIELD_TYPE]: (size, testId) => (
		<FieldIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[SENTIMENT_CF_TYPE]: (size, testId) => (
		<FieldIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[ORGANIZATION_CF_TYPE]: (size, testId) => (
		<FieldIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[SPRINT_CF_GLOBAL_TYPE]: (size, testId) => (
		<ReleaseIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[REQUEST_PARTICIPANTS_CF_TYPE]: (size, testId) => (
		<PeopleGroupIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[ORGANIZATIONS_CF_TYPE]: (size, testId) => (
		<OfficeBuildingIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[ATTACHMENT_FIELD_TYPE]: (size, testId) => (
		<AttachmentIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[PRIORITY_FIELD_TYPE]: (size, testId) => (
		<SortOptionsIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[TIME_TRACKING_FIELD_TYPE]: (size, testId) => (
		<StopwatchIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[ASSETS_CF_TYPE]: (size, testId) => (
		<ScreenIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[STATUS_FIELD_TYPE]: (size, testId) => (
		<ArrowRightIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[SUMMARY_FIELD_TYPE]: (size, testId) => (
		<TextLetterCaseIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[TEXT_CF_TYPE]: (size, testId) => (
		<TextLetterCaseIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[DESCRIPTION_FIELD_TYPE]: (size, testId) => (
		<AlignTextLeftIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[TEXT_AREA_CF_TYPE]: (size, testId) => (
		<AlignTextLeftIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[ENVIRONMENT_FIELD_TYPE]: (size, testId) => (
		<AlignTextLeftIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[MESSAGE_EDIT_CF_TYPE]: (size, testId) => (
		<AlignTextLeftIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[MESSAGE_VIEW_CF_TYPE]: (size, testId) => (
		<AlignTextLeftIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[MULTI_CHECKBOXES_CF_TYPE]: (size, testId) => (
		<CheckboxCheckedIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[COMMENT_FIELD_TYPE]: (size, testId) => (
		<CommentIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[APPROVERS_LIST_CF_TYPE]: (size, testId) => (
		<PersonAddedIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[APPROVAL_DATA_FIELD_TYPE]: (size, testId) => (
		<PersonAddedIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[PROJECT_PICKER_CF_TYPE]: (size, testId) => (
		<FolderClosedIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
	[CATEGORY_TYPE]: (size, testId) => (
		<MenuIcon label="" spacing="spacious" testId={testId} color={token('color.icon')} size={size} />
	),
	[TEAMS_PLATFORM_CF_TYPE]: (size, testId) => (
		<TeamsIcon label="" spacing="spacious" testId={testId} size={size} />
	),
	[GOALS_CF_TYPE]: (size, testId) => (
		<GoalIcon label="" spacing="spacious" testId={testId} color={token('color.icon')} size={size} />
	),
	[ATLAS_PROJECT_CF_TYPE]: (size, testId) =>
		fg('atlassian_projects_admin_icon') ? (
			<ProjectIcon
				label=""
				spacing="spacious"
				testId={testId}
				color={token('color.icon')}
				size={size}
			/>
		) : (
			<FieldIcon
				label=""
				spacing="spacious"
				testId={testId}
				color={token('color.icon')}
				size={size}
			/>
		),
	[FORMULA_NUMBER_CF_TYPE]: (size, testId) => (
		<DataFormulaIcon
			label=""
			spacing="spacious"
			testId={testId}
			color={token('color.icon')}
			size={size}
		/>
	),
};

const getNewIconForType = (
	type: string,
	size: 'small' | 'medium',
	testId?: string,
): ReactElement | null => {
	const iconRenderer = newIconMapping[type];
	if (!iconRenderer) {
		return null;
	}

	return iconRenderer(size, testId);
};

export const StrictFieldTypeIcon = memo(({ type, size = 'medium', testId }: FieldTypeIconProps) => {
	if (fg('enghealth-custom-icon-migration')) {
		const newIcon = getNewIconForType(type, size, testId);
		if (newIcon) {
			return newIcon;
		}
		return <FieldIcon label="" spacing="spacious" testId={testId} color={token('color.icon')} />;
	}

	if (type === TEAMS_PLATFORM_CF_TYPE) {
		return <TeamsIcon label="" size={size} spacing="spacious" testId={testId} />;
	}

	if (type === GOALS_CF_TYPE) {
		return <GoalIcon label="" spacing="spacious" testId={testId} color={token('color.icon')} />;
	}

	if (type === ATLAS_PROJECT_CF_TYPE && fg('atlassian_projects_admin_icon')) {
		return <ProjectIcon label="" spacing="spacious" testId={testId} color={token('color.icon')} />;
	}

	if (type === FORMULA_NUMBER_CF_TYPE && fg('frontier-formula-field-d1')) {
		return <DataFormulaIcon label="" testId={testId} color={token('color.icon')} />;
	}

	return (
		// eslint-disable-next-line @atlaskit/design-system/no-custom-icons
		<Icon
			label=""
			glyph={checkedFieldTypesGlyphs[type] || checkedFieldTypesGlyphs.customField}
			size={size}
			testId={testId}
		/>
	);
});

export const FieldTypeIcon = memo(({ type, size = 'medium', testId }: FieldTypeIconProps) => {
	if (fg('enghealth-custom-icon-migration')) {
		const newIcon = getNewIconForType(type, size, testId);
		if (newIcon) {
			return newIcon;
		}
		// Fallback to default field icon
		return <FieldIcon label="" spacing="spacious" testId={testId} color={token('color.icon')} />;
	}

	// Legacy behavior when feature gate is disabled
	if (type === TEAMS_PLATFORM_CF_TYPE) {
		const iconSpacing = size === 'small' ? 'compact' : 'spacious';

		return <TeamsIcon label="" size={size} spacing={iconSpacing} testId={testId} />;
	}

	if (type === GOALS_CF_TYPE) {
		return <GoalIcon label="" spacing="spacious" testId={testId} color={token('color.icon')} />;
	}

	if (type === ATLAS_PROJECT_CF_TYPE && fg('atlassian_projects_admin_icon')) {
		return <ProjectIcon label="" spacing="spacious" testId={testId} color={token('color.icon')} />;
	}

	if (type === FORMULA_NUMBER_CF_TYPE && fg('frontier-formula-field-d1')) {
		return <DataFormulaIcon label="" spacing="spacious" color={token('color.icon')} />;
	}

	return (
		// eslint-disable-next-line @atlaskit/design-system/no-custom-icons
		<Icon
			label=""
			glyph={allFieldTypeGlyphs[type] || allFieldTypeGlyphs.customField}
			size={size}
			testId={testId}
		/>
	);
});

/**
 * @deprecated - Use allFieldTypeGlyphs or FieldTypeIcon instead.
 */
export default allIcons;
